'use client';

import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> & {
    label?: string;
    description?: string;
    size?: 'sm' | 'default' | 'lg';
  }
>(({ className, label, description, size = 'default', id, ...props }, ref) => {
  const generatedId = React.useId();
  const switchId = id || generatedId;

  const sizes = {
    sm: { root: 'h-5 w-9', thumb: 'h-4 w-4 data-[state=checked]:translate-x-4' },
    default: { root: 'h-6 w-11', thumb: 'h-5 w-5 data-[state=checked]:translate-x-5' },
    lg: { root: 'h-7 w-14', thumb: 'h-6 w-6 data-[state=checked]:translate-x-7' },
  };

  return (
    <div className="flex items-center justify-between gap-3">
      {(label || description) && (
        <div className="grid gap-0.5">
          {label && (
            <label
              htmlFor={switchId}
              className="text-sm font-medium text-[var(--text-primary)] cursor-pointer"
            >
              {label}
            </label>
          )}
          {description && (
            <p className="text-xs text-[var(--text-tertiary)]">{description}</p>
          )}
        </div>
      )}

      <SwitchPrimitive.Root
        ref={ref}
        id={switchId}
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-pill',
          'border-2 border-transparent',
          'transition-all duration-200',
          // States
          'data-[state=unchecked]:bg-[var(--border-default)]',
          'data-[state=checked]:bg-[var(--primary)]',
          // Focus
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 focus-visible:ring-offset-2',
          // Disabled
          'disabled:cursor-not-allowed disabled:opacity-50',
          sizes[size].root,
          className
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block rounded-pill bg-white shadow-raised',
            'transition-transform duration-200',
            'data-[state=unchecked]:translate-x-0.5',
            sizes[size].thumb
          )}
        />
      </SwitchPrimitive.Root>
    </div>
  );
});
Switch.displayName = 'Switch';

export { Switch };

